import React from "react";
import profileBackground from "@/assets/profile-background.svg";

const AccountSkeleton: React.FC = () => {
  return (
    <div className="flex flex-col w-full h-screen overflow-hidden bg-white">
      {/* Header Background */}
      <div className="w-full min-h-[200px] relative">
        <div className="w-full h-full bg-neutral-200 rounded-xl animate-pulse"></div>
      </div>

      {/* Main Content */}
      <div className="relative px-2 -mt-16">
        <div className="w-full">
          {/* Profile Header */}
          <div className="flex items-start justify-between mb-4 w-full pt-4">
            {/* Left side - Profile Image and Basic Info */}
            <div className="flex flex-col h-full w-full">
              <div className="flex items-start space-x-4 pl-4">
                <div className="relative -translate-y-4">
                  <div className="w-36 h-36 rounded-full bg-neutral-200 animate-pulse border-8 border-white shadow" />
                </div>
                <div className="flex flex-col space-y-2 ml-6">
                  <div className="pt-16 flex flex-row space-x-2.5 items-center">
                    <div className="h-8 w-48 bg-neutral-200 rounded-lg animate-pulse" />
                    <div className="w-2 h-2 bg-neutral-300 rounded-full"></div>
                    <div className="h-6 w-32 bg-neutral-200 rounded-lg animate-pulse" />
                  </div>
                  <div className="h-8 w-28 bg-neutral-200 rounded-lg animate-pulse -translate-x-2" />
                </div>
              </div>
            </div>

            {/* Right side - Stats */}
            <div className="pt-16 flex space-x-6">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-10 w-32 bg-neutral-200 rounded-xl animate-pulse"
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Profile Content Section */}
      <div className="w-full flex rounded-md gap-8 h-[calc(100vh-1rem)] overflow-hidden">
        {/* Main Content - 70% width */}
        <div className="w-[70%] bg-white rounded-xl border-bottom border-neutral-100">
          {/* Navigation Tabs */}
          <div className="flex items-center gap-2.5 pb-2.5 border-b border-neutral-200">
            {[{ width: "w-24", icon: "fa-regular fa-compass" }].map(
              (tab, i) => (
                <div
                  key={i}
                  className={`h-8 ${tab.width} bg-indigo-50 rounded-md animate-pulse flex items-center justify-center gap-2`}
                >
                  <div className="w-4 h-4 bg-indigo-200 rounded-full animate-pulse"></div>
                  <div className="h-4 w-12 bg-indigo-200 rounded animate-pulse"></div>
                </div>
              )
            )}
          </div>

          {/* Content Area */}
          <div className="py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="w-full rounded-lg overflow-hidden flex-shrink-0 border border-neutral-100 shadow-sm hover:shadow-md transition-all duration-300"
                >
                  <div className="relative">
                    <div className="w-full h-40 bg-neutral-200 animate-pulse" />
                    {/* Location badge */}
                    <div className="absolute top-2 left-2 bg-neutral-800/60 backdrop-blur-sm px-2 py-1 rounded-full animate-pulse flex items-center space-x-1">
                      <div className="w-3 h-3 bg-neutral-400 rounded-full animate-pulse" />
                      <div className="h-3 w-20 bg-neutral-300/80 rounded-full animate-pulse" />
                    </div>
                  </div>
                  <div className="p-2 space-y-2">
                    <div className="h-5 w-3/4 bg-neutral-200 animate-pulse rounded" />
                    <div className="flex items-center justify-between mt-1">
                      <div className="flex items-center space-x-4">
                        {/* Engagement icons */}
                        {[
                          { color: "bg-neutral-400" },
                          { color: "bg-neutral-400" },
                          { color: "bg-neutral-400" },
                        ].map((icon, j) => (
                          <div key={j} className="flex items-center space-x-2">
                            <div
                              className={`w-4 h-4 rounded-full ${icon.color} animate-pulse`}
                            />
                            <div className="w-3 h-3 bg-neutral-200 animate-pulse rounded" />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Similar Travelers Section - 30% width */}
        <div className="w-[30%] bg-white rounded-xl flex flex-col">
          {/* Header */}
          <div className="px-3 py-1.5 text-sm font-medium rounded-md flex items-center gap-2 bg-indigo-100 w-44 animate-pulse">
            <div className="w-4 h-4 bg-indigo-300 rounded-full animate-pulse" />
            <div className="h-4 w-32 bg-indigo-300 rounded animate-pulse" />
          </div>

          <div className="bg-white flex flex-col border border-neutral-200 rounded-lg mt-2 p-2 flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto scrollbar-hide rounded-2xl">
              <div className="space-y-4">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div
                    key={i}
                    className="w-full flex flex-row justify-between items-center space-x-3 p-2 rounded-lg hover:bg-neutral-100 transition-colors"
                  >
                    <div className="flex flex-row items-center space-x-3">
                      {/* Avatar */}
                      <div className="w-10 h-10 bg-neutral-200 rounded-full animate-pulse" />
                      {/* Traveler Info */}
                      <div className="flex flex-col items-start space-y-1">
                        <div className="h-4 w-24 bg-neutral-200 rounded animate-pulse" />
                        <div className="h-3 w-16 bg-neutral-200 rounded animate-pulse" />
                      </div>
                    </div>

                    {/* Follow Button */}
                    <div className="rounded-lg p-2 w-16 h-8 bg-neutral-100 border border-neutral-200 animate-pulse" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountSkeleton;
